import { api as wiredApi, tokenStore as wiredTokenStore } from './index';

/**
 * A rider's account on InGo's API (the mobile plan's M2; InGo ADR 0006 and
 * ADR 0014): signing in and out, registering, choosing a password, the
 * application's standing and the documents. The token goes to the secure
 * store here and nowhere else; every other screen reads the rider through
 * these calls, never a stored id.
 *
 * Built from its two collaborators so a test can hand it fakes, as the
 * client's own tests do; `riderApi` below is the app's wired copy.
 */
export function createRiderApi({ api, tokenStore }) {
  /** Stores the token first: an account without its token is not a session. */
  async function begin(data) {
    await tokenStore.write(data.token);

    return {
      account: data.account,
      mustChangePassword: Boolean(data.must_change_password),
      application: data.application ?? null,
    };
  }

  return {
    /** `POST rider/auth/login`. A wrong password, an unknown address and a closed account answer alike. */
    async signIn(email, password) {
      const { data } = await api.post('/rider/auth/login', { email, password });

      return begin(data);
    },

    /**
     * `POST rider/registrations`: the account made active, so the rider goes
     * straight on to their documents with the token it answers.
     */
    async register(details) {
      const { data } = await api.post('/rider/registrations', details);

      return begin(data);
    },

    /**
     * `POST rider/auth/deletion` (InGo ADR 0038), as `customerApi.requestDeletion`:
     * deleted in thirty days unless kept from the emailed link; every session
     * ends on the server, and the token is forgotten here.
     */
    async requestDeletion(confirmation) {
      const { data, message } = await api.post('/rider/auth/deletion', confirmation);
      await tokenStore.clear();

      return { dueOn: data?.due_on ?? null, message };
    },

    /**
     * Ends this device's session on the server, then here, whatever the server
     * said: a rider pressing "Sign out" is signed out.
     */
    async signOut() {
      try {
        await api.post('/rider/auth/logout', {});
      } catch {
        // Offline or already refused: the token is forgotten below either way.
      } finally {
        await tokenStore.clear();
      }
    },

    /** A password carried over from the old app is replaced before anything else opens. */
    async choosePassword(currentPassword, password) {
      await api.patch('/rider/auth/password', {
        current_password: currentPassword,
        password,
        password_confirmation: password,
      });
    },

    /** `GET rider/me`: what the rider registered with, identity and vehicle (ingo-e0, 2 Oct 2026). Read only. */
    async me() {
      const { data } = await api.get('/rider/me');

      return data;
    },

    /** Whether the rider may work, and if not, the sentence to show them. */
    async application() {
      const { data } = await api.get('/rider/me/application');

      return data;
    },

    /** The four documents, the empty ones included, and how far along they are. */
    async documents() {
      const { data, meta } = await api.get('/rider/me/documents');

      return { slots: data, compliance: meta?.compliance ?? null };
    },

    /** One document, as a photo or a PDF; a licence with the day it expires. */
    async sendDocument(type, file, expiresAt = null) {
      const form = new FormData();
      form.append('type', type);
      form.append('file', file);
      if (expiresAt) form.append('expires_at', expiresAt);

      const { data } = await api.post('/rider/me/documents', undefined, { form });

      return data;
    },
  };
}

export const riderApi = createRiderApi({ api: wiredApi, tokenStore: wiredTokenStore });
